import { useEffect, useMemo, useRef, useState } from "react";
import type { FieldContent } from "@capture/shared";
import { useZoneEditorStore } from "../state/zone-editor-store.js";
import { useZoneTemplate } from "../hooks/useZoneTemplate.js";
import {
  useFormConfigStore,
  resolveV3,
  activeForm as resolveActiveForm,
} from "../form-config/state/store.js";
import {
  ZoneEditorCanvas,
  type ZoneEditorCanvasHandle,
} from "./zone-editor/ZoneEditorCanvas.js";
import { ZoneFieldList } from "./zone-editor/ZoneFieldList.js";
import { ZoneEditorToolbar } from "./zone-editor/ZoneEditorToolbar.js";
import { ZoneEditorPageNav } from "./zone-editor/ZoneEditorPageNav.js";
import { MarkGroupsPanel } from "./zone-editor/MarkGroupsPanel.js";
import { ZoneViewportControls } from "./zone-editor/ZoneViewportControls.js";
import { ConfirmModal } from "@/components/ConfirmModal";
import { toast } from "@/components/Toaster";
import "./ZonesTab.css";

/**
 * Task 4 — The Zones tab is now per-form. It reads the ACTIVE form's fields
 * straight from the form-config store (the same source FieldListPane uses) and
 * writes drawn boxes back onto those fields' `zoneDefinition` via the store's
 * `updateField`. There is no longer a `fieldDefinitions` prop or an
 * `onSaveZones` callback — the store is the single source of truth, and the
 * template hook is scoped to the active form (`?form=`).
 */
interface ZonesTabProps {
  jobTypeId: string;
  /**
   * The form currently in scope. `null` resolves to the default form via the
   * same fallback the store's `activeForm` helper uses. Both the field list and
   * the per-form `ZoneTemplate` (via `useZoneTemplate`) are scoped to it.
   */
  activeFormId: string | null;
}

interface ZoneDef {
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
  isHandwriting?: boolean;
}

const AUTOSAVE_DELAY_MS = 600;

export function ZonesTab({ jobTypeId, activeFormId }: ZonesTabProps) {
  const {
    templateId,
    isLoading: storeLoading,
    isDirty,
    zoneMappings,
    pages,
    pageMatchingStrategy,
    reset,
    loadTemplate,
    syncFieldDefinitions,
    markClean,
  } = useZoneEditorStore();

  // ── Per-form field source (mirrors FieldListPane) ───────────────────────
  // contentV3 is the source of truth; `content` is the derived legacy view.
  // resolveV3 migrates a legacy-only seed on the fly (test/pre-load paths).
  // The active form falls back to the default form when activeFormId is
  // null/stale — the same fallback the store's mutating actions use, so the
  // read path (field list) and write path (updateField) can never drift.
  const contentV3 = useFormConfigStore((s) => s.contentV3);
  const legacyContent = useFormConfigStore((s) => s.content);
  // updateFormFields takes an explicit formId so the write targets the SAME
  // form we resolved for the read below — not the store's `activeFormId`, which
  // can lag behind this prop (e.g. FormConfigPage leaves the singleton on a
  // non-default form). Using the id-scoped action keeps read and write from
  // drifting onto different forms.
  const updateFormFields = useFormConfigStore((s) => s.updateFormFields);
  // Marksense v2 — mark-group mode declarations live on the FORM (they span
  // fields); the panel below writes them via the same id-scoped discipline.
  const updateForm = useFormConfigStore((s) => s.updateForm);
  const v3 = resolveV3({ contentV3, content: legacyContent });
  const form = v3 ? resolveActiveForm(v3, activeFormId) : null;

  // Derive the canvas/right-panel field list from the active form's fields.
  // Memoized on the form reference (stable while contentV3 is unchanged) so a
  // re-render driven by the zone-editor store doesn't recreate the array and
  // re-fire the sync effect below.
  const fieldDefinitions = useMemo(
    () =>
      (form?.fields ?? []).map((f) => ({
        id: f.fieldDefinitionId,
        name: f.name,
        label: f.label,
        ocrExtracted: f.ocrExtracted ?? false,
        zoneDefinition: (f.zoneDefinition as ZoneDef | null) ?? null,
      })),
    [form],
  );

  const {
    isLoading: apiLoading,
    fetchTemplate,
    uploadTemplate,
    deleteTemplate,
    updateSettings,
    updatePageAnchor,
  } = useZoneTemplate(jobTypeId, activeFormId ?? undefined);

  const [isSaving, setIsSaving] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<ZoneEditorCanvasHandle>(null);
  const hasLoaded = useRef(false);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Initial mount: reset + load template once per jobType ───────────────
  useEffect(() => {
    if (hasLoaded.current) return;
    hasLoaded.current = true;

    reset();

    fetchTemplate().then((data) => {
      if (!data) return; // no template yet — keep the dropzone state

      const rawPages = data.pages as Array<{
        pageNumber: number;
        imageUrl: string;
        imageWidth: number;
        imageHeight: number;
        anchorText?: string | null;
      }>;

      // R-125 (5.5) — Seed the field list from the *current* fieldDefinitions
      // snapshot. The reactive sync effect below keeps it in step with later
      // updates from the form-config draft.
      const initialMappings = fieldDefinitions.map((fd) => ({
        fieldDefinitionId: fd.id,
        fieldName: fd.name,
        fieldLabel: fd.label,
        ocrExtracted: fd.ocrExtracted,
        zone: (fd.zoneDefinition as ZoneDef | null) ?? null,
        isDirty: false,
      }));

      loadTemplate({
        templateId: String(data.id),
        pageMatchingStrategy: String(data.pageMatchingStrategy ?? "page_number"),
        pages: rawPages.map((p) => ({
          pageNumber: p.pageNumber,
          imageUrl: p.imageUrl,
          imageWidth: p.imageWidth,
          imageHeight: p.imageHeight,
          anchorText: p.anchorText ?? null,
          anchorDirty: false,
        })),
        zoneMappings: initialMappings,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobTypeId]);

  // ── R-125 (5.4 + 5.5) — Reactive sync of labels / OCR flag from the draft ─
  // The previous one-shot mount effect captured `fieldDefinitions` in its
  // closure at first render, when `rawFields` was still empty (the draft
  // hadn't loaded). The result was an empty right panel after reload AND
  // stale labels stuck on whatever was in the first paint ("New Field").
  // Re-syncing every time the prop changes fixes both.
  useEffect(() => {
    if (!templateId) return; // dropzone state — nothing to sync
    if (fieldDefinitions.length === 0) return;
    syncFieldDefinitions(
      fieldDefinitions.map((fd) => ({
        fieldDefinitionId: fd.id,
        name: fd.name,
        label: fd.label,
        ocrExtracted: fd.ocrExtracted,
        // R-175 — thread server-side zoneDefinition through so a publish
        // that re-mints FieldDefinition.id (draft FieldContent.id → prod
        // FieldDefinition.id) repopulates zones from the server instead
        // of nulling them out via the new-entry fallback.
        zoneDefinition: (fd.zoneDefinition as ZoneDef | null) ?? null,
      })),
    );
  }, [fieldDefinitions, templateId, syncFieldDefinitions]);

  // ── Warn on unsaved changes ────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const handleUpload = async (file: File) => {
    setUploadError(null);
    try {
      await uploadTemplate(file);

      // F-013 — the upload response's page rows can lead the rasterized page
      // image (the proxy URL isn't ready the instant upload returns), so
      // loading straight from the upload response left the canvas blank until
      // a manual reload. Re-fetch the canonical template (exactly what a reload
      // does) so the image URL + dimensions are the ready, authoritative ones.
      const data = await fetchTemplate();
      if (!data) {
        setUploadError("Template uploaded but could not be loaded — try reloading.");
        return;
      }

      const rawPages = data.pages as Array<{
        pageNumber: number;
        imageUrl: string;
        imageWidth: number;
        imageHeight: number;
        anchorText?: string | null;
      }>;

      const initialMappings = fieldDefinitions.map((fd) => ({
        fieldDefinitionId: fd.id,
        fieldName: fd.name,
        fieldLabel: fd.label,
        ocrExtracted: fd.ocrExtracted,
        zone: null,
        isDirty: false,
      }));

      loadTemplate({
        templateId: String(data.id),
        pageMatchingStrategy: String(data.pageMatchingStrategy ?? "page_number"),
        pages: rawPages.map((p) => ({
          pageNumber: p.pageNumber,
          imageUrl: p.imageUrl,
          imageWidth: p.imageWidth,
          imageHeight: p.imageHeight,
          anchorText: p.anchorText ?? null,
          anchorDirty: false,
        })),
        zoneMappings: initialMappings,
      });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed");
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Save dirty zones onto the active form's fields. The canvas works in
      // fieldDefinitionId; merge the dirty boxes into the resolved form's field
      // list (matching on fieldDefinitionId) and write the whole list back via
      // the id-scoped `updateFormFields`. Dirty zones whose field is no longer
      // on this form simply never match in the map below — skipped, no throw.
      const dirtyZones = zoneMappings.filter((zm) => zm.isDirty);
      if (form && dirtyZones.length > 0) {
        const dirtyById = new Map(dirtyZones.map((zm) => [zm.fieldDefinitionId, zm.zone]));
        const nextFields = form.fields.map((f) =>
          dirtyById.has(f.fieldDefinitionId)
            ? {
                ...f,
                zoneDefinition: dirtyById.get(f.fieldDefinitionId) as FieldContent["zoneDefinition"],
              }
            : f,
        );
        updateFormFields(form.id, nextFields);
      }

      // Save dirty anchor texts
      const dirtyPages = pages.filter((p) => p.anchorDirty);
      for (const page of dirtyPages) {
        await updatePageAnchor(page.pageNumber, page.anchorText ?? "");
      }

      // Save strategy
      await updateSettings(pageMatchingStrategy);

      markClean();
    } finally {
      setIsSaving(false);
    }
  };

  // R-125 (5.9) — auto-save dirty zones after a brief debounce so drawn
  // boxes survive a reload without the operator having to find the Save
  // button. We still expose Save in the toolbar for manual commit (anchor
  // text / strategy changes go through the same handler).
  useEffect(() => {
    if (!templateId) return;
    if (!isDirty) return;
    if (isSaving) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      void handleSave();
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
    // We intentionally re-arm on isDirty edge transitions only; pulling in
    // handleSave / dependencies it touches creates a thrash loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, templateId]);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleDelete = () => setConfirmDeleteOpen(true);

  const handleConfirmDelete = async () => {
    setConfirmDeleteOpen(false);
    const ok = await deleteTemplate();
    if (ok) {
      reset();
      hasLoaded.current = false;
    }
  };

  const handleReprocess = async () => {
    toast.info("Re-running OCR on template — this may take a moment.");
  };

  const isLoading = storeLoading || apiLoading;

  // State 1: Loading
  if (isLoading) {
    return <div className="zones-loading-state">Loading template…</div>;
  }

  // State 2: No template
  if (!templateId) {
    return (
      <div className="zones-dropzone-wrapper">
        <div
          className="zones-dropzone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const file = e.dataTransfer.files[0];
            if (file) handleUpload(file);
          }}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
          }}
        >
          <div className="zones-dropzone__icon">📄</div>
          <div className="zones-dropzone__heading">Upload a template document</div>
          <div className="zones-dropzone__description">
            Upload a template document to define extraction zones.
          </div>
          <div className="zones-dropzone__hint">TIFF, PDF, PNG, or JPEG</div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".tif,.tiff,.pdf,.png,.jpg,.jpeg"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUpload(file);
              e.target.value = "";
            }}
          />
        </div>
        {uploadError && <div className="zones-dropzone__error">{uploadError}</div>}
      </div>
    );
  }

  // State 3: Template loaded — full editor
  return (
    <div className="zones-editor-shell">
      <ConfirmModal
        open={confirmDeleteOpen}
        title="Delete the template document?"
        description="All zone mappings will be cleared. This cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
      <ZoneEditorToolbar
        onSave={handleSave}
        onReprocess={handleReprocess}
        onDelete={handleDelete}
        isSaving={isSaving}
      />
      <div className="zones-editor-viewport-bar">
        <ZoneViewportControls
          getCanvasSize={() => canvasRef.current?.getCanvasSize() ?? null}
          getPageSize={() => canvasRef.current?.getPageSize() ?? null}
        />
      </div>

      <div className="zones-editor-main">
        {/* Canvas area */}
        <div className="zones-editor-canvas-col">
          <div className="zones-editor-canvas-wrap">
            <ZoneEditorCanvas ref={canvasRef} />
          </div>
          <ZoneEditorPageNav />
        </div>

        {/* Field list (two sections) + mark-group modes */}
        <div className="zones-editor-side-col">
          <ZoneFieldList />
          {form && (
            <MarkGroupsPanel
              formId={form.id}
              markGroups={form.markGroups ?? []}
              onUpdateForm={updateForm}
            />
          )}
        </div>
      </div>
    </div>
  );
}
